/**
 * The dashboard sidebar's widths, in one place.
 *
 * These numbers are not the sidebar's business alone: every full-bleed view
 * offsets itself past the sidebar by hand (`width: calc(100% - X)` plus a
 * matching `marginLeft`), because the sidebar is a fixed-position drawer
 * rather than a flex sibling of the content. That offset used to be a literal
 * repeated in ~25 view files, so the width could not be changed without
 * finding all of them -- and any that were missed would silently slide under
 * the sidebar or leave a gutter.
 *
 * The expanded width now depends on a user preference (one-level vs the
 * two-level rail + panel), so the views read it as a CSS variable that the
 * Dashboard layout sets on `:root`, rather than as a number baked in at import
 * time. Import `SIDEBAR_WIDTH` / `SIDEBAR_RAIL_WIDTH` and use them as complete
 * CSS lengths -- `calc(100% - ${SIDEBAR_WIDTH})`, never `${SIDEBAR_WIDTH}px`.
 */

/** One-level sidebar, expanded: wide enough for a deal name at `body1`. */
export const ONE_LEVEL_SIDEBAR_PX = 288;

/**
 * The icon rail: the collapsed one-level sidebar, and the first level of the
 * two-level sidebar. Wide enough for a group's icon with a short label (`My
 * Org`) on one line underneath it.
 */
export const RAIL_PX = 80;

/** The second level of the two-level sidebar: the full nav, beside the rail. */
export const TWO_LEVEL_PANEL_PX = 256;

/** The expanded sidebar's total width for the given nav mode, in px. */
export const expandedSidebarPx = (twoLevel) =>
  twoLevel ? RAIL_PX + TWO_LEVEL_PANEL_PX : ONE_LEVEL_SIDEBAR_PX;

/** Expanded sidebar, as a CSS length. Follows the two-level preference. */
export const SIDEBAR_WIDTH = `var(--dc-sidebar-width, ${ONE_LEVEL_SIDEBAR_PX}px)`;

/** Collapsed rail, as a CSS length: icon plus its tap target, no panel. */
export const SIDEBAR_RAIL_WIDTH = `var(--dc-sidebar-rail-width, ${RAIL_PX}px)`;
